"use client";
import EmbeddedStripeContainer from "@/app/stripe/EmbeddedStripeContainer";
import PricingBlock from "@/components/ui/Pricing/PricingBlock";
import { useFetchCurrentUserQuery } from "@/hooks/QueryHooks/ReactQueryWrapperhooks";
import { cn } from "@/lib/utils";
import { BasePricingBlockProps, InternalSubscriptionPriceID } from "@/typings";
import Constants from "@/utils/Constants";
import { useSearchParams } from "next/navigation";
import { useState } from "react";

type PricingPageProps = {
  loggedInUser: boolean;
};

export default function PlansPage({ loggedInUser }: PricingPageProps) {
  const searchParams = useSearchParams();
  const { data: fetchedUser } = useFetchCurrentUserQuery(loggedInUser);
  let currentUserPlan: InternalSubscriptionPriceID | undefined =
    fetchedUser == undefined
      ? undefined
      : Constants.localSubPlanToPriceID(fetchedUser.currentPlan);

  const [currentPlan, setCurrentPlan] = useState(currentUserPlan);
  function renderHeader() {
    return (
      <div className="mx-auto w-full text-center md:w-11/12 xl:w-9/12">
        <h1 className="bg-gradient-to-r from-secondary to-warning bg-clip-text pb-2 text-4xl font-extrabold leading-none tracking-normal text-transparent md:text-6xl md:tracking-tight">
          Pricing that works for every budget
        </h1>
      </div>
    );
  }
  function renderPayAsYouGoPricing() {
    return (
      <>
        {Constants.payAsYouGoOptions.map((x, index) => {
          return (
            <div key={index}>
              <PricingBlock
                {...x}
                userAlreadyHasPlan={currentUserPlan != undefined}
                isCurrentPlan={false}
                receivedClientSecretCallback={(serverClientSecret) => {
                  setClientSecret(serverClientSecret);
                }}
              />
            </div>
          );
        })}
      </>
    );
  }
  function getTargetPlansDict() {
    let appSource = searchParams.get("mlapp");
    return appSource == undefined
      ? Constants.monthlyPricingOptionsDict
      : Constants.socialManMonthlyPricingOptionsDict;
  }
  function getTargetPlansOptions() {
    let appSource = searchParams.get("mlapp");
    return appSource == undefined
      ? Constants.monthlyPricingOptions
      : Constants.socialManMonthlyPricingOptions;
  }
  function getCorrectCTA(x: BasePricingBlockProps) {
    if (currentPlan == null) return null;
    if (currentPlan == x.internalPriceID) {
      return "Cancel Plan";
    } else {
      let targetPlansDict = getTargetPlansDict();
      let currentUserPlanData = targetPlansDict.get(currentPlan);
      if (currentUserPlanData == null) return null;
      let xPlanData = targetPlansDict.get(
        x.internalPriceID as InternalSubscriptionPriceID
      );
      if (xPlanData == null) return null;
      if (currentUserPlanData.cost > xPlanData.cost) {
        return "Downgrade Plan";
      }
      if (currentUserPlanData.cost < xPlanData.cost) {
        return "Upgrade Plan";
      }
    }
  }
  function renderSubscriptionPlans() {
    let targetPlansOptions = getTargetPlansOptions();
    return (
      <>
        {targetPlansOptions.map((x, index) => {
          let expectedCTA = getCorrectCTA(x) ?? x.CTA;
          return (
            <div key={index}>
              <PricingBlock
                {...x}
                CTA={expectedCTA}
                userAlreadyHasPlan={currentUserPlan != undefined}
                isCurrentPlan={currentPlan == x.internalPriceID}
                planChangedCallback={(newPlan: InternalSubscriptionPriceID) => {
                  setCurrentPlan(newPlan);
                }}
              />
            </div>
          );
        })}
      </>
    );
  }
  function renderPlansBlocks() {
    return (
      <div className="flex w-full">
        <div className={cn("grid w-full grid-cols-1 gap-8", `md:grid-cols-2`)}>
          {renderSubscriptionPlans()}
        </div>
      </div>
    );
  }
  function renderPayAsYouGoBlocks() {
    const gridColsClass = `lg:grid-cols-${Constants.payAsYouGoOptions.length}`;
    return (
      <div className="flex w-full">
        <div className={cn("grid w-full grid-cols-1 gap-8", gridColsClass)}>
          {renderPayAsYouGoPricing()}
        </div>
      </div>
    );
  }
  const [clientSecret, setClientSecret] = useState<string | undefined>(
    undefined
  );
  function renderEmbeddedStripe() {
    return <EmbeddedStripeContainer clientSecret={clientSecret} />;
  }
  return (
    <section className="mx-auto my-auto flex h-full w-9/12 flex-col justify-evenly space-y-4 p-12 px-4 ">
      {renderHeader()}
      {renderPlansBlocks()}
      {/* {renderPayAsYouGoBlocks()} */}
      {renderEmbeddedStripe()}
    </section>
  );
}
